// Workstation → Compliance — the Advertising Claims & Substantiation
// Specialist's review of ABH creative.
//
// ADVISORY, NEVER A GREEN LIGHT. `check_compliance` never blocks and never
// clears anything for launch (the locked Compliance doctrine). This screen
// names risk for a human; nothing on it may read as approval, which is why
// there is no "approved" state and why the advisory line is not dismissible.
//
// FOUR STATES, KEPT APART. "nobody looked" is not "looked and found nothing":
// an unreviewed script reads `Not reviewed`, never clean. A failed review reads
// as failed, never as clean either.
import React, { useCallback, useEffect, useState } from "react";

import EmptyState from "../../components/EmptyState";
import PageHead from "../../components/PageHead";
import * as api from "./api";
import OfferPicker from "./OfferPicker";
import { Panel } from "./panels";

const LABEL = {
  reviewed_flagged: "Findings",
  reviewed_clean: "No flags raised",
  review_failed: "Review failed",
  not_reviewed: "Not reviewed",
};
const TONE = {
  reviewed_flagged: "warn",
  reviewed_clean: "ok",
  review_failed: "crit",
  not_reviewed: "unknown",
};

function ScriptRow({ item, onReview, busy }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="ws-compliance-row">
      <div className="ws-compliance-head">
        <span className="ws-mono">{item.script_id}</span>
        <span className="ws-tag" data-tone={TONE[item.status]}>
          {LABEL[item.status] || item.status}
        </span>
        {item.platform ? <span className="ws-dim">{item.platform}</span> : null}
        <button
          type="button"
          className="btn btn-sm"
          disabled={busy}
          onClick={() => onReview(item.script_id)}
        >
          {busy ? "Reviewing…" : item.status === "not_reviewed" ? "Review" : "Re-review"}
        </button>
        {item.report || item.error ? (
          <button type="button" className="btn-ghost btn-sm"
                  onClick={() => setOpen((v) => !v)}>
            {open ? "Hide" : "Show"} report
          </button>
        ) : null}
      </div>
      {open && item.error ? (
        <p className="ws-error">The review did not complete: {item.error}</p>
      ) : null}
      {open && item.report ? (
        <pre className="ws-compliance-report">{item.report}</pre>
      ) : null}
    </div>
  );
}

// `selection` is resolved ONCE in `workstation.jsx` and handed to whichever
// tab is showing, so switching tabs keeps the offer and no tab owns it. This
// follows that contract rather than keeping a second, private offer state.
export default function Compliance({ selection }) {
  const { offers, selectedId, select } = selection || {};
  const offerId = selectedId || null;
  const [data, setData] = useState(null);
  const [state, setState] = useState("idle");
  const [busyId, setBusyId] = useState("");
  const [loadError, setLoadError] = useState(null);

  const load = useCallback(async () => {
    if (!offerId) return;
    setState("loading");
    const res = await api.getComplianceReview(offerId);
    if (res.ok) {
      setData(res.data);
      setState("ready");
      setLoadError(null);
    } else {
      // Rule 10: a failure must be distinguishable from a normal result, and it
      // must say WHICH failure. The old branch stored `res.status` — which on
      // success is `undefined` and on failure is an HTTP number — into the same
      // variable the render read as a state name, so the screen said
      // "Could not read the reviews ()." for every outcome including success.
      setData(null);
      setState("failed");
      setLoadError(
        [res.status ? `HTTP ${res.status}` : null, res.error]
          .filter(Boolean).join(" — ") || "the request did not complete"
      );
    }
  }, [offerId]);

  useEffect(() => { load(); }, [load]);

  const review = async (scriptId) => {
    setBusyId(scriptId);
    await api.runComplianceReview(scriptId);
    setBusyId("");
    load();
  };

  return (
    <>
      <PageHead
        title="Compliance"
        subtitle="The Advertising Claims & Substantiation Specialist's review of ABH creative. Advisory only — it names risk, it does not approve anything."
      />
      <OfferPicker offers={offers} selectedId={offerId} onSelect={select} />

      {!offerId ? <EmptyState title="Pick an offer." /> : null}

      {offerId && state === "loading" && !data ? (
        <p className="ws-dim">Reading reviews…</p>
      ) : null}

      {offerId && state === "failed" ? (
        <p className="ws-error" role="alert">
          Could not read the reviews: {loadError}. This is a failure, not an
          empty result — nothing about this offer&apos;s compliance is known
          from this screen right now.
        </p>
      ) : null}

      {data && !data.in_scope ? (
        <Panel title="Not in scope">
          <p>{data.out_of_scope_reason}</p>
        </Panel>
      ) : null}

      {data && data.in_scope ? (
        <Panel
          title={`Scripts — ${data.offer_code || data.offer_id}`}
          subtitle={
            `${data.counts.reviewed_flagged} with findings · ` +
            `${data.counts.reviewed_clean} no flags · ` +
            `${data.counts.review_failed} failed · ` +
            `${data.counts.not_reviewed} not reviewed`
          }
        >
          <p className="ws-advisory">{data.advisory_note}</p>
          {data.items.length === 0 ? (
            <EmptyState title="This offer has no scripts yet." />
          ) : (
            data.items.map((i) => (
              <ScriptRow key={i.script_id} item={i} onReview={review}
                         busy={busyId === i.script_id} />
            ))
          )}
        </Panel>
      ) : null}
    </>
  );
}
